import {
	Box,
	Typography,
	Modal,
	Divider,
	Grid,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BasicTable from '../Table/Table';
import {styled} from '@mui/material/styles';
import styles from './AllPartsModal.module.scss';
import clsx from 'clsx';
import {pxToRem, responsiveFontSizes} from '../../utils/getFontValue';
import {DARK_MODE} from '../../constants';

const classNames = {
	modalContainer: {
		width: '70%',
		height: '80%',
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		display: 'flex',
		flexDirection: 'column',
		px: 6,
		py: 4,
		background: (theme) => theme.palette.background.paper,
		boxShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 8px 40px rgba(0, 0, 0, 0.24), 0px 4px 20px rgba(0, 0, 0, 0.12)'
			: '0px 8px 40px rgba(45, 74, 45, 0.12), 0px 4px 20px rgba(45, 74, 45, 0.08)',
		borderRadius: '16px',
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(255, 255, 255, 0.1)'
			: '1px solid rgba(45, 74, 45, 0.1)',
		backdropFilter: 'blur(20px)',
		position: 'relative',
		overflow: 'hidden',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: '0',
			left: '0',
			right: '0',
			bottom: '0',
			background: (theme) => theme.palette.mode === 'dark'
				? 'linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 50%, rgba(255, 255, 255, 0.02) 100%)'
				: 'linear-gradient(135deg, rgba(45, 74, 45, 0.02) 0%, rgba(45, 74, 45, 0.01) 50%, rgba(45, 74, 45, 0.02) 100%)',
			borderRadius: '16px',
			pointerEvents: 'none',
		}
	},
	paper: {
		overflow: 'hidden',
		flex: 1,
		background: (theme) => theme.palette.background.default,
		borderRadius: '12px',
		boxShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 4px 20px rgba(0, 0, 0, 0.15), inset 0px 1px 0px rgba(255, 255, 255, 0.1)'
			: '0px 4px 20px rgba(45, 74, 45, 0.08), inset 0px 1px 0px rgba(255, 255, 255, 0.2)',
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(255, 255, 255, 0.08)'
			: '1px solid rgba(45, 74, 45, 0.08)',
		backdropFilter: 'blur(10px)',
		position: 'relative',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: '0',
			left: '0',
			right: '0',
			bottom: '0',
			background: (theme) => theme.palette.mode === 'dark'
				? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01))'
				: 'linear-gradient(135deg, rgba(45, 74, 45, 0.02), rgba(45, 74, 45, 0.01))',
			borderRadius: '12px',
			pointerEvents: 'none',
		}
	},
	heading: {
		color: (theme) => theme.palette.primary.main,
		fontSize: pxToRem(12),
		...responsiveFontSizes({sm: 12, md: 14, lg: 18}),
		fontWeight: 700,
		letterSpacing: '0.5px',
		textShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 1px 2px rgba(0, 0, 0, 0.2)'
			: '0px 1px 2px rgba(45, 74, 45, 0.1)',
	},
	text: {
		letterSpacing: 0.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: 500,
		fontSize: pxToRem(15),
		...responsiveFontSizes({sm: 15, md: 17, lg: 21}),
		color: (theme) => theme.palette.text.primary,
		textShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 1px 2px rgba(0, 0, 0, 0.1)'
			: '0px 1px 2px rgba(45, 74, 45, 0.05)',
	},
};

const StyledTableCell = styled(TableCell)(({theme}) => ({
	overflow: 'hidden',
	border: 'none',
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: (theme) => theme.palette.background.paper,
		color: theme.palette.primary.main,
		fontWeight: 700,
		letterSpacing: '0.5px',
		textShadow: theme.palette.mode === 'dark'
			? '0px 1px 2px rgba(0, 0, 0, 0.2)'
			: '0px 1px 2px rgba(45, 74, 45, 0.1)',
		boxShadow: theme.palette.mode === 'dark'
			? '0px 2px 8px rgba(0, 0, 0, 0.1)'
			: '0px 2px 8px rgba(45, 74, 45, 0.05)',
		borderBottom: theme.palette.mode === 'dark'
			? '2px solid rgba(255, 255, 255, 0.1)'
			: '2px solid rgba(45, 74, 45, 0.1)',
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14,
		fontWeight: 500,
		color: theme.palette.text.primary,
		textShadow: theme.palette.mode === 'dark'
			? '0px 1px 2px rgba(0, 0, 0, 0.1)'
			: '0px 1px 2px rgba(45, 74, 45, 0.05)',
		transition: 'all 0.3s ease',
		'&:hover': {
			backgroundColor: theme.palette.mode === 'dark'
				? 'rgba(255, 255, 255, 0.05)'
				: 'rgba(45, 74, 45, 0.02)',
			transform: 'translateY(-1px)',
			boxShadow: theme.palette.mode === 'dark'
				? '0px 4px 12px rgba(0, 0, 0, 0.2)'
				: '0px 4px 12px rgba(45, 74, 45, 0.1)',
		},
	},
}));

const StyledTableRow = styled(TableRow)(({theme}) => ({
	'&:nth-of-type(even)': {
		background: (theme) => theme.palette.background.default,
	},
	'&:nth-of-type(odd)': {
		background: (theme) => theme.palette.background.paper,
	},
	transition: 'all 0.3s ease',
	'&:hover': {
		transform: 'scale(1.01)',
		boxShadow: theme.palette.mode === 'dark'
			? '0px 8px 25px rgba(0, 0, 0, 0.15)'
			: '0px 8px 25px rgba(45, 74, 45, 0.08)',
		borderRadius: '8px',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

const COLUMNS = [
	{
		width: 50,
		field: '#',
		renderHeader: () => <Typography sx={classNames.heading}>#</Typography>,
		render: ({partIndex}) => (
      <Typography sx={classNames.text}>{partIndex + 1}</Typography>
    ),
	},
	{
		width: 300,
		field: 'partFamily',
		renderHeader: () => (
			<Typography sx={classNames.heading}>Part Family</Typography>
		),
		render: ({part}) => (
			<Typography sx={classNames.text}>
				{part.familyName}
			</Typography>
		),
	},
	{
		width: 300,
		field: 'partNumber',
		renderHeader: () => (
			<Typography sx={classNames.heading}>Part Number</Typography>
		),
		render: ({part}) => (
			<Typography
				sx={
					(classNames.text,
					{
						whiteSpace: 'nowrap',
					})
				}
			>
				{part.partNumber}
			</Typography>
		),
	},
];

const AllPartsModal = ({
	open,
	handleClose,
	rows,
	columns,
	defaultColumns,
	title,
	subtitle,
	scannableParts,
}) => {
	return (
		<Modal
			open={open}
			sx={{
				background: (theme) => theme.palette.mode === DARK_MODE 
					? 'rgba(0, 0, 0, 0.6)' 
					: 'rgba(45, 74, 45, 0.4)',
				backdropFilter: 'blur(10px)',
			}}
		>
			<Box
				sx={{
					...classNames.modalContainer,
					'&::before': {
						content: '""',
						position: 'absolute',
						top: '-2px',
						left: '-2px',
						right: '-2px',
						bottom: '-2px',
						background: (theme) => theme.palette.mode === 'dark'
							? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))'
							: 'linear-gradient(135deg, rgba(45, 74, 45, 0.1), rgba(45, 74, 45, 0.02))',
						borderRadius: '18px',
						zIndex: -1,
						filter: 'blur(4px)',
					}
				}}
			>
				<Grid
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						paddingBottom: 2,
						mb: 2,
					}}
				>
					<Typography 
						variant='h4' 
						sx={{
							color: (theme) => theme.palette.primary.main,
							fontWeight: 800,
							letterSpacing: '1px',
							textShadow: (theme) => theme.palette.mode === 'dark'
								? '0px 2px 4px rgba(0, 0, 0, 0.3)'
								: '0px 2px 4px rgba(45, 74, 45, 0.2)',
							fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
							position: 'relative',
							'&::after': {
								content: '""',
								position: 'absolute',
								bottom: '-6px',
								left: '0',
								right: '0',
								height: '3px',
								background: (theme) => theme.palette.mode === 'dark'
									? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
									: 'linear-gradient(90deg, transparent, rgba(45, 74, 45, 0.3), transparent)',
								borderRadius: '2px',
							}
						}}
					>
						{title}
					</Typography>
					<IconButton
						sx={{
							color: (theme) => theme.palette.text.primary,
							background: (theme) => theme.palette.mode === 'dark'
								? 'rgba(255, 255, 255, 0.1)'
								: 'rgba(45, 74, 45, 0.05)',
							borderRadius: '12px',
							boxShadow: (theme) => theme.palette.mode === 'dark'
								? '0px 4px 12px rgba(0, 0, 0, 0.2)'
								: '0px 4px 12px rgba(45, 74, 45, 0.1)',
							border: (theme) => theme.palette.mode === 'dark'
								? '1px solid rgba(255, 255, 255, 0.2)'
								: '1px solid rgba(45, 74, 45, 0.1)',
							transition: 'all 0.3s ease',
							'&:hover': {
								background: (theme) => theme.palette.mode === 'dark'
									? 'rgba(255, 255, 255, 0.2)'
									: 'rgba(45, 74, 45, 0.1)',
								transform: 'scale(1.05)',
								boxShadow: (theme) => theme.palette.mode === 'dark'
									? '0px 6px 20px rgba(0, 0, 0, 0.3)'
									: '0px 6px 20px rgba(45, 74, 45, 0.15)',
							}
						}}
						onClick={handleClose}
					>
						<CloseIcon sx={{width: '35px', height: '35px'}} />
					</IconButton>
				</Grid>
				<Typography
					variant='h6'
					sx={{
						color: (theme) => theme.palette.primary.main,
						fontWeight: 600,
						letterSpacing: '0.5px',
						textShadow: (theme) => theme.palette.mode === 'dark'
							? '0px 2px 4px rgba(0, 0, 0, 0.2)'
							: '0px 2px 4px rgba(45, 74, 45, 0.1)',
						mb: 3,
						position: 'relative',
						'&::after': {
							content: '""',
							position: 'absolute',
							bottom: '-4px',
							left: '0',
							right: '0',
							height: '2px',
							background: (theme) => theme.palette.mode === 'dark'
								? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
								: 'linear-gradient(90deg, transparent, rgba(45, 74, 45, 0.2), transparent)',
							borderRadius: '1px',
						}
					}}
				>
					{subtitle}
				</Typography>

				<BasicTable
					listId='allPartsTable'
					rows={rows}
					columns={columns}
					defaultColumns={defaultColumns}
					tableStyles={{paper: classNames.paper}}
				>
					<Divider 
						sx={{
							background: (theme) => theme.palette.mode === 'dark'
								? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
								: 'linear-gradient(90deg, transparent, rgba(45, 74, 45, 0.2), transparent)',
							height: '2px',
							borderRadius: '1px',
							my: 2,
						}}
					/>
					<TableContainer
						component={Paper}
						className={clsx(styles.scrollBar)}
						sx={{
							height: '70%', 
							background: 'none',
							borderRadius: '12px',
							boxShadow: (theme) => theme.palette.mode === 'dark'
								? '0px 4px 20px rgba(0, 0, 0, 0.15), inset 0px 1px 0px rgba(255, 255, 255, 0.1)'
								: '0px 4px 20px rgba(45, 74, 45, 0.08), inset 0px 1px 0px rgba(255, 255, 255, 0.2)',
							border: (theme) => theme.palette.mode === 'dark'
								? '1px solid rgba(255, 255, 255, 0.08)'
								: '1px solid rgba(45, 74, 45, 0.05)',
							backdropFilter: 'blur(5px)',
							position: 'relative',
							'&::before': {
								content: '""',
								position: 'absolute',
								top: '0',
								left: '0',
								right: '0',
								bottom: '0',
								background: (theme) => theme.palette.mode === 'dark'
									? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01))'
									: 'linear-gradient(135deg, rgba(45, 74, 45, 0.02), rgba(45, 74, 45, 0.01))',
								borderRadius: '12px',
								pointerEvents: 'none',
							}
						}}
					>
						<Table sx={{tableLayout: 'fixed'}} stickyHeader>
							<TableHead>
								<TableRow>
									{COLUMNS.map(col => (
										<StyledTableCell
											sx={{width: `${col.width}px`, maxWidth: `${col.width}px`}}
										>
											{col.renderHeader()}
										</StyledTableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{scannableParts.map((part, index) => {
									return (
										<StyledTableRow>
                      {COLUMNS.map(
                        ({width, styles, otherProps, render}) => {
                          const rowStyles = styles
                            ? {
                                ...styles(
                                  {part, partIndex: index}
                                ),
                              }
                            : {};

                          const props = otherProps
                            ? {
                                ...otherProps(
                                  {part, partIndex: index}
                                ),
                              }
                            : {};

                          return (
                            <StyledTableCell
                              sx={{
                                width: width,
                                maxWidth: width,
                                ...rowStyles,
                              }}
                              {...props}
                            >
                              {render(
                                {part, partIndex: index}
                              )}
                            </StyledTableCell>
                          );
                        },
                      )}
										</StyledTableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
					<Divider 
						sx={{
							background: (theme) => theme.palette.mode === 'dark'
								? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
								: 'linear-gradient(90deg, transparent, rgba(45, 74, 45, 0.2), transparent)',
							height: '2px',
							borderRadius: '1px',
							my: {xs: 2, md: 3},
						}}
					/>
				</BasicTable>
			</Box>
		</Modal>
	);
};

export default AllPartsModal;
